import { createTheme } from '@mui/material/styles';

// ── Armenian Earth Palette ──────────────────────────────────────────────────
// Inspired by Armenian landscapes: volcanic tuff stone, apricot orchards,
// Mount Ararat snowcaps, Dilijan forest, ancient monastery walls.
//
//  Primary   → Matte terracotta-orange  (warm, earthy, welcoming)
//  Secondary → Deep forest green        (Armenian mountains & forests)
//  Accent    → Antique gold             (khachkar engravings, sunlight)
//  Background→ Warm off-white parchment (tuff stone, aged manuscript)

export const activeTheme = {
  '--primary-color':    '#C4622D',   // Matte terracotta-orange (Armenian tuff)
  '--secondary-color':  '#4A6741',   // Deep Dilijan forest green
  '--accent-color':     '#C9973A',   // Antique gold (khachkar, apricot)

  '--bg-color':         '#F7F3EE',   // Warm parchment
  '--card-bg':          '#FFFFFF',
  '--surface-muted':    '#EDE8E1',   // Light stone wash

  '--text-main':        '#2C1F14',   // Deep walnut
  '--text-secondary':   '#6B503A',   // Warm mid-brown

  '--border-color':     'rgba(196, 98, 45, 0.15)',

  '--border-radius':    '12px',
  '--border-radius-lg': '20px',

  '--font-family':      "'Inter', 'Segoe UI', sans-serif",
};

// ── MUI Theme ───────────────────────────────────────────────────────────────
export function createAppTheme() {
  return createTheme({
    palette: {
      primary: {
        main:         '#C4622D',   // Terracotta orange
        light:        '#D98456',
        dark:         '#9A4A1F',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main:         '#4A6741',   // Forest green
        light:        '#6A8F61',
        dark:         '#334A2D',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#D4860A',           // Amber-gold
      },
      info: {
        main: '#5B8DB8',           // Muted sky (Lake Sevan)
      },
      success: {
        main: '#4A8C5C',           // Sage green
      },
      error: {
        main: '#C0392B',
      },
      background: {
        default: '#F7F3EE',
        paper:   '#FFFFFF',
      },
      text: {
        primary:   '#2C1F14',
        secondary: '#6B503A',
      },
    },
    typography: {
      // Inter for UI, Playfair Display for headings (loaded via App.css @import)
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      h1: { fontWeight: 900 },
      h2: { fontWeight: 800 },
      h3: { fontWeight: 800 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          containedPrimary: {
            background: 'linear-gradient(135deg, #C4622D 0%, #9A4A1F 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #D4723D 0%, #AA5A2F 100%)' },
          },
          containedSecondary: {
            background: 'linear-gradient(135deg, #4A6741 0%, #334A2D 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #5A7751 0%, #435A3D 100%)' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0 2px 12px rgba(44, 31, 20, 0.08)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { borderRadius: 16 } },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
      },
      MuiTab: {
        styleOverrides: { root: { fontWeight: 600, textTransform: 'none' } },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#C4622D' },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: '#C4622D' },
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: 'rgba(196, 98, 45, 0.12)' } },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 12 } },
      },
    },
  });
}